import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1;
const TAP_ZOOM = 2.5;
const DRAG_THRESHOLD = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  #scale = DEFAULT_ZOOM;
  #initialDistance = 0;
  #startScale = DEFAULT_ZOOM;
  #translate = { x: 0, y: 0 };
  #startPosition = { x: 0, y: 0 };
  #startTranslate = { x: 0, y: 0 };
  #isDragging = false;
  #initialized = false;
  #animationFrame = null;
  #hasDraggedBeyondThreshold = false;
  #hasManualZoom = false;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;
    this.#resizeObserver.observe(this);
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;

    // Touch (mobile)
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: false, signal });
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: false, signal });
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: false, signal });

    // Mouse (desktop)
    this.addEventListener('mousedown', this.#handleMouseDown, { signal });
    this.addEventListener('mouseup', this.#handleMouseUp, { signal });

    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    if (this.#animationFrame) cancelAnimationFrame(this.#animationFrame);
  }

  #handleResize = () => {
    if (this.#initialized) this.#requestUpdateTransform();
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  // ── DESKTOP MOUSE ──────────────────────────────────────────────

  #mouseDownPos = { x: 0, y: 0 };
  #mouseMoved = false;

  #handleMouseDown = (event) => {
    this.#mouseDownPos = { x: event.clientX, y: event.clientY };
    this.#mouseMoved = false;
  };

  #handleMouseUp = (event) => {
    const dx = event.clientX - this.#mouseDownPos.x;
    const dy = event.clientY - this.#mouseDownPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Only treat as click if mouse didn't move much
    if (dist < 5) {
      this.#toggleZoom(event.clientX, event.clientY);
    }
  };

  // ── SHARED ZOOM TOGGLE ─────────────────────────────────────────

  #toggleZoom(clientX, clientY) {
    const isZoomedIn = this.#scale > MIN_ZOOM + 0.05;

    if (isZoomedIn) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.#hasManualZoom = false;
    } else {
      const oldScale = this.#scale;
      this.#scale = TAP_ZOOM;
      const cx = this.clientWidth / 2;
      const cy = this.clientHeight / 2;
      const dx = clientX - cx;
      const dy = clientY - cy;
      const delta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (dx * delta) / this.#scale;
      this.#translate.y -= (dy * delta) / this.#scale;
      this.#hasManualZoom = false;
    }

    this.#requestUpdateTransform();
  }

  // ── TOUCH ──────────────────────────────────────────────────────

  #handleTouchStart = (event) => {
    preventDefault(event);
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const t1 = event.touches[0];
      const t2 = event.touches[1];
      if (!t1 || !t2) return;
      this.#initialDistance = getDistance(t1, t2);
      this.#startScale = this.#scale;
      this.#isDragging = false;
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;
      this.#startPosition = { x: touch.clientX, y: touch.clientY };
      this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
      this.#isDragging = true;
      this.#hasDraggedBeyondThreshold = false;
    }
  };

  #handleTouchMove = (event) => {
    preventDefault(event);
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const t1 = event.touches[0];
      const t2 = event.touches[1];
      if (!t1 || !t2) return;

      const midX = (t1.clientX + t2.clientX) / 2;
      const midY = (t1.clientY + t2.clientY) / 2;
      const currentDistance = getDistance(t1, t2);
      const oldScale = this.#scale;

      this.#scale = clamp((currentDistance / this.#initialDistance) * this.#startScale, MIN_ZOOM, MAX_ZOOM);
      this.#hasManualZoom = true;

      const cx = this.clientWidth / 2;
      const cy = this.clientHeight / 2;
      const delta = this.#scale / oldScale - 1.0;
      this.#translate.x -= ((midX - cx) * delta) / this.#scale;
      this.#translate.y -= ((midY - cy) * delta) / this.#scale;

      this.#requestUpdateTransform();
      this.#isDragging = false;

    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (!touch) return;

      const dist = getDistance(touch, this.#startPosition);
      if (!this.#hasDraggedBeyondThreshold && dist < DRAG_THRESHOLD) return;
      this.#hasDraggedBeyondThreshold = true;

      const dx = touch.clientX - this.#startPosition.x;
      const dy = touch.clientY - this.#startPosition.y;
      this.#translate.x = this.#startTranslate.x + dx / this.#scale;
      this.#translate.y = this.#startTranslate.y + dy / this.#scale;
      this.#requestUpdateTransform();
    }
  };

  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;

      // Tap (no drag) → toggle zoom
      if (!this.#hasDraggedBeyondThreshold && !this.#hasManualZoom) {
        const touch = event.changedTouches[0];
        if (touch) this.#toggleZoom(touch.clientX, touch.clientY);
      }

      // Reset manual zoom flag after pinch ends
      this.#hasManualZoom = false;
      this.#requestUpdateTransform();
      this.#hasDraggedBeyondThreshold = false;
    }
  };

  // ── CONSTRAINTS ────────────────────────────────────────────────

  #constrainTranslation() {
    if (!this.#image) return;
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate = { x: 0, y: 0 };
      return;
    }

    const rect = this.getBoundingClientRect();
    const img = this.#image;
    const nw = img.naturalWidth || rect.width;
    const nh = img.naturalHeight || rect.width;

    const imgRatio = nw / nh;
    const wrapRatio = rect.width / rect.height;

    let iw, ih;
    if (imgRatio > wrapRatio) {
      iw = rect.width;
      ih = iw / imgRatio;
    } else {
      ih = rect.height;
      iw = ih * imgRatio;
    }

    const maxX = Math.max(0, iw * this.#scale - rect.width) / 2 / this.#scale;
    const maxY = Math.max(0, ih * this.#scale - rect.height) / 2 / this.#scale;

    this.#translate.x = clamp(this.#translate.x, -maxX, maxX);
    this.#translate.y = clamp(this.#translate.y, -maxY, maxY);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  #resetZoom = () => {
    this.#scale = DEFAULT_ZOOM;
    this.#startScale = DEFAULT_ZOOM;
    this.#translate = { x: 0, y: 0 };
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
    this.#hasManualZoom = false;
    this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  };

  destroy() {
    this.#controller.abort();
    if (this.#animationFrame) cancelAnimationFrame(this.#animationFrame);
  }
}

function getDistance(point1, point2) {
  const x1 = point1.x ?? point1.clientX;
  const y1 = point1.y ?? point1.clientY;
  const x2 = point2.x ?? point2.clientX;
  const y2 = point2.y ?? point2.clientY;
  return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */